// src/components/sections/About.js
import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';
import { FileDown } from 'lucide-react';
import { motion } from 'framer-motion';

const About = () => {
  const { darkMode } = useTheme();
  const { language } = useLanguage();
  const t = translations[language];
  
  return (
    <section id="about" className="py-16">
      <div className="container mx-auto px-6">
        <motion.h2 
          className="text-3xl font-bold mb-12 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {t["about.title"]}
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-2xl font-semibold mb-4">Igor Vannel SIBEMOU TIENTCHEU</h3>
            <h4 className={`text-xl mb-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              {t["about.job"]}
            </h4>
            
            <p className="mb-6 text-lg">
              {t["about.description1"]}
            </p>
            
            <p className="mb-6">
              {t["about.description2"]}
            </p>
            
            <div className="mb-8">
              <h5 className="font-semibold mb-3 text-lg">{t["about.techSkills"]}</h5>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-medium mb-2">{t["about.languages"]}</p>
                  <ul className="space-y-1">
                    <li className="flex items-start">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-2 mr-2 ${darkMode ? 'bg-blue-400' : 'bg-blue-600'}`}></span>
                      Java, JavaScript, TypeScript
                    </li>
                    <li className="flex items-start">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-2 mr-2 ${darkMode ? 'bg-blue-400' : 'bg-blue-600'}`}></span>
                      PHP, HTML, CSS
                    </li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-2">{t["about.frameworks"]}</p>
                  <ul className="space-y-1">
                    <li className="flex items-start">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-2 mr-2 ${darkMode ? 'bg-blue-400' : 'bg-blue-600'}`}></span>
                      Spring Boot, React JS
                    </li>
                    <li className="flex items-start">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-2 mr-2 ${darkMode ? 'bg-blue-400' : 'bg-blue-600'}`}></span>
                      React Native, Django
                    </li>
                    <li className="flex items-start">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-2 mr-2 ${darkMode ? 'bg-blue-400' : 'bg-blue-600'}`}></span>
                      Ionic, Cordova
                    </li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-2">{t["about.databases"]}</p>
                  <ul className="space-y-1">
                    <li className="flex items-start">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-2 mr-2 ${darkMode ? 'bg-blue-400' : 'bg-blue-600'}`}></span>
                      PostgreSQL, MySQL
                    </li>
                    <li className="flex items-start">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-2 mr-2 ${darkMode ? 'bg-blue-400' : 'bg-blue-600'}`}></span>
                      SQL Developer, NoSQL, MongoDB
                    </li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-2">{t["about.tools"]}</p>
                  <ul className="space-y-1">
                    <li className="flex items-start">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-2 mr-2 ${darkMode ? 'bg-blue-400' : 'bg-blue-600'}`}></span>
                      Git, GitHub, GitLab, Bitbucket
                    </li>
                    <li className="flex items-start">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-2 mr-2 ${darkMode ? 'bg-blue-400' : 'bg-blue-600'}`}></span>
                      Jira, Postman, Insomnia
                    </li>
                    <li className="flex items-start">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-2 mr-2 ${darkMode ? 'bg-blue-400' : 'bg-blue-600'}`}></span>
                      Agile/Scrum, CI/CD
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="mb-8">
              <h5 className="font-semibold mb-3 text-lg">{t["about.education"]}</h5>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className={`inline-block w-2 h-2 rounded-full mt-2 mr-2 ${darkMode ? 'bg-blue-400' : 'bg-blue-600'}`}></span>
                  <div>
                    <p className="font-medium">Licence fondamentale en informatique</p>
                    <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                      Université de Dschang, 2023
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className={`inline-block w-2 h-2 rounded-full mt-2 mr-2 ${darkMode ? 'bg-blue-400' : 'bg-blue-600'}`}></span>
                  <div>
                    <p className="font-medium">Baccalauréat de l'enseignement général</p>
                    <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                      Série Technologie de l'Information au Lycée Classique de Bafoussam
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => window.open('/assets/cv.pdf', '_blank')}
                className={`px-6 py-3 rounded-lg ${
                  darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                } text-white transition-colors duration-200 flex items-center`}
              >
                <FileDown className="mr-2 h-5 w-5" />
                {t["about.downloadCV"]}
              </button>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="order-first md:order-last"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                <div className="text-center">
                  <div className={`text-4xl font-bold mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    10+
                  </div>
                  <p className="text-sm">{t["about.stats.projects"]}</p>
                </div>
              </div>
              
              <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                <div className="text-center">
                  <div className={`text-4xl font-bold mb-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                    7+
                  </div>
                  <p className="text-sm">{t["about.stats.clients"]}</p>
                </div>
              </div>
              
              <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                <div className="text-center">
                  <div className={`text-4xl font-bold mb-2 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                    3+
                  </div>
                  <p className="text-sm">{t["about.stats.experience"]}</p>
                </div>
              </div>
              
              <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                <div className="text-center">
                  <div className={`text-4xl font-bold mb-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    10+
                  </div>
                  <p className="text-sm">{t["about.stats.technologies"]}</p>
                </div>
              </div>
            </div>
            
            <div className={`mt-4 p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h5 className="font-semibold mb-3">{t["about.techFavorites"]}</h5>
              <div className="flex flex-wrap gap-2">
                <span className={`px-3 py-1 rounded-full text-xs ${
                  darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                }`}>Spring Boot</span>
                <span className={`px-3 py-1 rounded-full text-xs ${
                  darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                }`}>Java</span>
                <span className={`px-3 py-1 rounded-full text-xs ${
                  darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
                }`}>React JS</span>
                <span className={`px-3 py-1 rounded-full text-xs ${
                  darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
                }`}>React Native</span>
                <span className={`px-3 py-1 rounded-full text-xs ${
                  darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'
                }`}>TypeScript</span>
                <span className={`px-3 py-1 rounded-full text-xs ${
                  darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800'
                }`}>CI/CD</span>
                <span className={`px-3 py-1 rounded-full text-xs ${
                  darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800'
                }`}>Docker</span>
                <span className={`px-3 py-1 rounded-full text-xs ${
                  darkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                }`}>PostgreSQL</span>
                <span className={`px-3 py-1 rounded-full text-xs ${
                  darkMode ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-100 text-indigo-800'
                }`}>Django</span>
                <span className={`px-3 py-1 rounded-full text-xs ${
                  darkMode ? 'bg-teal-900 text-teal-300' : 'bg-teal-100 text-teal-800'
                }`}>Git</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;